import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Phone, MessageCircle, ShieldCheck, Crown, Lock, BadgeCheck, Sparkles, Flame } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES } from "@/lib/categories";
import { getUser } from "@/lib/user";
import { AlertPopup } from "@/components/AlertPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESEAUX = {
  orange: { nom: "Orange", couleur: "#FF7900", texte: "#fff" },
  mtn: { nom: "MTN", couleur: "#FFCC00", texte: "#1C1917" },
  moov: { nom: "Moov", couleur: "#0066B3", texte: "#fff" },
  autre: { nom: "Mobile", couleur: "#57534E", texte: "#fff" },
};

const FILTRES = [
  { id: "", label: "Tous" },
  { id: "vip", label: "ClubVIP" },
  { id: "love", label: "Love" },
  { id: "lago", label: "Lago" },
];

const ContactCard = ({ c, deverrouille, index }) => {
  const [clics, setClics] = useState(c.clics || 0);
  const reseau = RESEAUX[c.reseau] || RESEAUX.autre;
  const cat = CATEGORIES[c.category] || CATEGORIES.lago;
  const waLink = c.numero_e164 ? `https://wa.me/${c.numero_e164.replace(/[^0-9]/g, "")}` : null;
  const telLink = c.numero_e164 ? `tel:${c.numero_e164}` : null;

  const enregistrerClic = (type) => {
    setClics((n) => n + 1); // optimiste
    axios.post(`${API}/contacts-premium/${c.id}/clic`, { type }).catch(() => {});
  };

  // Pop-up de sécurité avant d'ouvrir le contact (membres payants)
  const [intent, setIntent] = useState(null); // "whatsapp" | "appel"
  const executerContact = () => {
    if (intent === "whatsapp" && waLink) {
      enregistrerClic("whatsapp");
      window.open(waLink, "_blank", "noopener,noreferrer");
    } else if (intent === "appel" && telLink) {
      enregistrerClic("appel");
      window.location.href = telLink;
    }
    setIntent(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.4) }}
      data-testid={`contact-card-${c.id}`}
      className="group relative bg-white rounded-3xl border border-[#E5DFD5] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(28,25,23,0.35)]"
    >
      {c.category === "vip" && (
        <span className="absolute -top-2.5 -right-2.5 inline-flex items-center gap-1 bg-gradient-to-r from-[#B8860B] to-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg">
          <Crown size={11} /> VIP
        </span>
      )}

      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center font-heading text-2xl font-extrabold text-white shadow-inner"
          style={{ background: cat.gradient || cat.color }}
        >
          {c.firstname?.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-heading font-extrabold text-stone-900 text-lg truncate">
              {c.firstname}{c.age ? `, ${c.age}` : ""}
            </h3>
            {c.verified && <BadgeCheck size={16} className="text-[#1D9BF0] shrink-0" />}
          </div>
          <p className="text-xs text-stone-500 truncate">
            {c.gender ? `${c.gender} · ` : ""}{c.commune || c.city}
          </p>
        </div>
        {clics > 0 && (
          <span
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#E05638] bg-[#E05638]/10 px-2.5 py-1 rounded-full"
            title="Nombre de fois contacté"
            data-testid={`contact-clics-${c.id}`}
          >
            <Flame size={12} /> {clics}
          </span>
        )}
      </div>

      {/* Numéro + opérateur */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <span
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
          style={{ backgroundColor: reseau.couleur, color: reseau.texte }}
        >
          {reseau.nom}
        </span>
        <span className={`font-heading font-bold tracking-wide ${deverrouille ? "text-stone-900" : "text-stone-400 blur-[1.5px] select-none"}`} data-testid={`contact-numero-${c.id}`}>
          {deverrouille ? c.numero : c.numero_masque}
        </span>
      </div>

      {/* Actions */}
      <div className="mt-5 grid grid-cols-2 gap-2.5">
        <a
          href={deverrouille ? waLink : undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { e.preventDefault(); if (deverrouille) setIntent("whatsapp"); }}
          data-testid={`contact-whatsapp-${c.id}`}
          className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-full transition-all duration-300 ${
            deverrouille ? "bg-[#0A5C36] hover:bg-[#084b2c] text-white group-hover:scale-[1.02]" : "bg-stone-100 text-stone-400 cursor-not-allowed"
          }`}
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <a
          href={deverrouille ? telLink : undefined}
          onClick={(e) => { e.preventDefault(); if (deverrouille) setIntent("appel"); }}
          data-testid={`contact-call-${c.id}`}
          className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-full transition-all duration-300 ${
            deverrouille ? "bg-[#E05638] hover:bg-[#C84428] text-white group-hover:scale-[1.02]" : "bg-stone-100 text-stone-400 cursor-not-allowed"
          }`}
        >
          <Phone size={15} /> Appeler
        </a>
      </div>
      {intent && (
        <AlertPopup category={c.category} onConfirm={executerContact} onClose={() => setIntent(null)} />
      )}
    </motion.div>
  );
};

export default function ContactsPremium() {
  const user = getUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categorie, setCategorie] = useState("");
  const [verifiesSeuls, setVerifiesSeuls] = useState(false);

  const charger = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/contacts-premium`, {
        params: { viewer_id: user?.id, category: categorie || undefined, verified_only: verifiesSeuls || undefined },
      })
      .then((r) => setData(r.data))
      .catch(() => setData({ deverrouille: false, total: 0, contacts: [] }))
      .finally(() => setLoading(false));
  }, [user?.id, categorie, verifiesSeuls]);

  useEffect(() => { charger(); }, [charger]);

  const deverrouille = data?.deverrouille;
  const contacts = data?.contacts || [];

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />

      {/* Hero premium */}
      <header className="relative overflow-hidden bg-[#12100E] pt-32 pb-16">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 20%, rgba(212,175,55,0.18), transparent 45%), radial-gradient(circle at 80% 60%, rgba(224,86,56,0.18), transparent 45%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
            <Sparkles size={14} /> Annuaire premium
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-4 tracking-tight" data-testid="contacts-title">
            Contactez nos membres
          </h1>
          <p className="text-stone-300 text-base mt-4 max-w-xl mx-auto">
            Numéros WhatsApp & appel des membres, avec leur opérateur. Réservé aux membres avec un abonnement actif.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-24 -mt-8 relative z-10" data-testid="contacts-premium-page">
        {/* Filtres */}
        <div className="bg-white rounded-2xl border border-[#E5DFD5] p-3 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <div className="flex flex-wrap gap-1.5">
            {FILTRES.map((f) => (
              <button
                key={f.id || "tous"}
                onClick={() => setCategorie(f.id)}
                data-testid={`contacts-filtre-${f.id || "tous"}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  categorie === f.id ? "bg-[#1C1917] text-white" : "text-stone-600 hover:bg-stone-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setVerifiesSeuls((v) => !v)}
            data-testid="contacts-filtre-verifies"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
              verifiesSeuls ? "bg-[#1D9BF0] text-white" : "text-stone-600 hover:bg-stone-100"
            }`}
          >
            <ShieldCheck size={15} /> Vérifiés uniquement
          </button>
        </div>

        {/* Bandeau verrouillage */}
        <AnimatePresence>
          {!loading && !deverrouille && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contacts-locked-banner"
              className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-[#1C1917] to-[#3a2f1a] border border-[#D4AF37]/30 p-5"
            >
              <div className="flex items-center gap-3 text-white">
                <div className="w-11 h-11 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Lock size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="font-heading font-bold">Numéros masqués</p>
                  <p className="text-sm text-stone-300">Activez un abonnement pour révéler tous les contacts et écrire sur WhatsApp.</p>
                </div>
              </div>
              <Link
                to="/abonnement"
                data-testid="contacts-cta-abonnement"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E05638] to-[#D4AF37] text-white font-bold px-6 py-3 rounded-full transition-transform hover:scale-105"
              >
                <Crown size={16} /> Débloquer les numéros
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compteur */}
        {!loading && (
          <p className="mt-6 text-sm text-stone-500" data-testid="contacts-count">
            <b className="text-stone-900">{contacts.length}</b> membre(s) avec numéro{deverrouille ? "" : " · aperçu masqué"}
          </p>
        )}

        {/* Grille */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-12 h-12 rounded-full border-4 border-[#E5DFD5] border-t-[#E05638] animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-24 text-stone-500" data-testid="contacts-vide">
            Aucun membre avec numéro pour ce filtre.
          </div>
        ) : (
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {contacts.map((c, i) => (
              <ContactCard key={c.id} c={c} deverrouille={c.deverrouille ?? deverrouille} index={i} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
